import { el, render, initials } from './dom.js';
import { ICONS } from './icons.js';

// ---------------------------------------------------------------------------
// UI building blocks for the redesigned portal (Figma: "CM — User Management
// and Admin Portal"). Plain DOM, no framework — every view composes these
// rather than hand-rolling markup, so a card, a badge or a modal looks and
// behaves the same everywhere.
//
// Styling lives in styles.css / admin.css; nothing here sets colours inline.
// ---------------------------------------------------------------------------

// --- icons ------------------------------------------------------------------

/**
 * A design icon, masked so it takes the surrounding text colour. Rendered at
 * the asset's own size unless the design places it larger (`size`).
 */
export function icon(name, { size, className = '', label } = {}) {
  const def = ICONS[name];
  if (def === undefined) throw new Error(`Unknown icon "${name}"`);
  const w = size ?? def.w;
  const h = size ?? def.h;
  return el('span', {
    class: `icon${className ? ` ${className}` : ''}`,
    style: `--icon:url("${def.url}");width:${w}px;height:${h}px`,
    role: label ? 'img' : null,
    'aria-label': label ?? null,
    'aria-hidden': label ? null : 'true',
  });
}

/** A multi-colour design asset (timeline nodes, illustrations) — drawn as-is, at its own size. */
export function art(name, { className = '' } = {}) {
  const def = ICONS[name];
  if (def === undefined) throw new Error(`Unknown asset "${name}"`);
  return el('img', { class: className || null, src: def.url, width: String(def.w), height: String(def.h), alt: '' });
}

// --- buttons ----------------------------------------------------------------

/**
 * variant: primary | secondary | danger | danger-outline | ghost
 * `iconName` renders a leading design icon.
 */
export function button(label, { variant = 'secondary', size, iconName, onClick, title, type = 'button', disabled, className = '' } = {}) {
  return el(
    'button',
    {
      type,
      class: `btn btn-${variant}${size === 'sm' ? ' btn-sm' : ''}${className ? ` ${className}` : ''}`,
      title: title ?? null,
      disabled: disabled ?? false,
      onclick: onClick ?? null,
    },
    iconName ? icon(iconName) : null,
    label,
  );
}

/** Square icon-only button (row actions, card "⋯", drawer close). */
export function iconButton(iconName, { label, onClick, className = '', size } = {}) {
  return el(
    'button',
    { type: 'button', class: `btn-icon${className ? ` ${className}` : ''}`, 'aria-label': label, title: label, onclick: onClick ?? null },
    icon(iconName, { size }),
  );
}

// --- badges, avatars, progress ---------------------------------------------

/** tone: green | amber | red | gray | blue | indigo. `dot` prefixes a status dot. */
export function badge(text, tone = 'gray', { dot = false, title } = {}) {
  return el(
    'span',
    { class: `badge badge--${tone}${dot ? ' badge--dot' : ''}`, title: title ?? null },
    dot ? el('span', { class: 'badge__dot' }) : null,
    text,
  );
}

/** Outlined role chip, as on the design's Users screen. */
export function roleChip(text) {
  return el('span', { class: 'role-chip' }, text);
}

/** Initials avatar — round for people, rounded-square for organisations. */
export function avatar(name, { square = false, size = 32 } = {}) {
  return el(
    'span',
    { class: `avatar${square ? ' avatar--square' : ''}`, style: `width:${size}px;height:${size}px` },
    initials(name),
  );
}

/** Thin progress track. pct is clamped to 0–100; tone: indigo | red | amber | green. */
export function progressBar(pct, { tone = 'indigo', thick = false } = {}) {
  const clamped = Math.max(0, Math.min(100, Number.isFinite(pct) ? pct : 0));
  return el(
    'span',
    { class: `progress${thick ? ' progress--thick' : ''}`, role: 'progressbar', 'aria-valuenow': String(Math.round(clamped)), 'aria-valuemin': '0', 'aria-valuemax': '100' },
    el('span', { class: `progress__fill progress__fill--${tone}`, style: `width:${clamped}%` }),
  );
}

/** Tone for a used/limit bar: red at 90%+, amber at 75%+, otherwise indigo. */
export function usageTone(pct) {
  if (pct >= 90) return 'red';
  if (pct >= 75) return 'amber';
  return 'indigo';
}

// --- page structure ---------------------------------------------------------

export function pageHeader({ title, subtitle, actions = [] }) {
  return el(
    'header',
    { class: 'page-head' },
    el('div', { class: 'page-head__text' }, el('h1', { class: 'page-title' }, title), subtitle ? el('p', { class: 'page-subtitle' }, subtitle) : null),
    actions.filter(Boolean).length > 0 ? el('div', { class: 'page-head__actions' }, actions.filter(Boolean)) : null,
  );
}

/** White rounded card with an optional header row (title + actions). */
export function card({ title, subtitle, actions = [], body, className = '', flush = false }) {
  return el(
    'section',
    { class: `card${flush ? ' card--flush' : ''}${className ? ` ${className}` : ''}` },
    title
      ? el(
          'div',
          { class: 'card__head' },
          el('div', {}, el('h2', { class: 'card__title' }, title), subtitle ? el('p', { class: 'card__subtitle' }, subtitle) : null),
          actions.filter(Boolean).length > 0 ? el('div', { class: 'card__actions' }, actions.filter(Boolean)) : null,
        )
      : null,
    body,
  );
}

/**
 * KPI card. tone picks the icon tile tint. `meta` is the line under the value
 * (a trend, a limit, a note); `progress` optionally draws a used/limit bar.
 */
export function kpiCard({ label, value, iconName, tone = 'indigo', meta, progress, title }) {
  return el(
    'div',
    { class: 'kpi', title: title ?? null },
    el(
      'div',
      { class: 'kpi__head' },
      el('span', { class: 'kpi__label' }, label),
      iconName ? el('span', { class: `kpi__icon kpi__icon--${tone}` }, icon(iconName)) : null,
    ),
    el('div', { class: 'kpi__value' }, value),
    progress ?? null,
    meta ? el('div', { class: 'kpi__meta' }, meta) : null,
  );
}

/** "↑ 12% vs previous 30 days" / "3 new in the last 30 days" — never a made-up trend. */
export function trendMeta({ trendPercent, last30Days }, noun = 'new') {
  if (trendPercent === null || trendPercent === undefined) {
    return el('span', { class: 'trend trend--flat' }, `${Number(last30Days ?? 0).toLocaleString()} ${noun} in the last 30 days`);
  }
  const up = trendPercent >= 0;
  return el(
    'span',
    {
      class: `trend ${up ? 'trend--up' : 'trend--down'}`,
      title: `${Number(last30Days ?? 0).toLocaleString()} ${noun} in the last 30 days, compared with the 30 days before.`,
    },
    up ? icon('kpi-trend-up') : null,
    el('strong', {}, `${up ? '↑' : '↓'} ${Math.abs(trendPercent)}%`),
    el('span', { class: 'trend__muted' }, 'vs prior 30 days'),
  );
}

/** Label/value rows, as in the design's "Organization Information" card. */
export function infoList(rows) {
  return el(
    'dl',
    { class: 'info-list' },
    rows
      .filter(Boolean)
      .map(([label, value]) =>
        el('div', { class: 'info-row' }, el('dt', {}, label), el('dd', {}, value === null || value === undefined || value === '' ? '—' : value)),
      ),
  );
}

/** One "Usage Limits" row: label, "used / limit (pct%)", and a bar. */
export function usageRow({ label, used, limit, note }) {
  const pct = limit > 0 ? (used / limit) * 100 : used > 0 ? 100 : 0;
  return el(
    'div',
    { class: 'usage' },
    el(
      'div',
      { class: 'usage__meta' },
      el('span', { class: 'usage__label' }, label),
      el('span', { class: 'usage__value' }, note ?? `${used.toLocaleString()} / ${limit.toLocaleString()} (${Math.round(pct)}%)`),
    ),
    progressBar(pct, { tone: usageTone(pct), thick: true }),
  );
}

/** Underline tabs. items: [{ id, label, count? }]. */
export function tabs(items, activeId, onSelect) {
  return el(
    'div',
    { class: 'tabs', role: 'tablist' },
    items.map((t) =>
      el(
        'button',
        {
          type: 'button',
          role: 'tab',
          class: `tab${t.id === activeId ? ' is-active' : ''}`,
          'aria-selected': t.id === activeId ? 'true' : 'false',
          onclick: () => onSelect(t.id),
        },
        t.label,
        t.count !== undefined && t.count !== null ? el('span', { class: 'tab__count' }, String(t.count)) : null,
      ),
    ),
  );
}

// --- filters ----------------------------------------------------------------

/** Search box with the design's leading icon. `onSearch` is debounced. */
export function searchField({ placeholder, value = '', onSearch, delay = 250, label }) {
  const input = el('input', {
    class: 'search-field__input',
    type: 'search',
    placeholder,
    value,
    'aria-label': label ?? placeholder,
  });
  let timer;
  input.addEventListener('input', () => {
    clearTimeout(timer);
    timer = setTimeout(() => onSearch(input.value.trim()), delay);
  });
  return { root: el('label', { class: 'search-field' }, icon('search'), input), input };
}

/** "Status: All ▾" pill — a native select, styled, so keyboard and screen readers just work. */
export function filterSelect({ label, options, value, onChange }) {
  const select = el(
    'select',
    { class: 'filter__select', 'aria-label': label, onchange: (e) => onChange(e.target.value) },
    options.map((o) => el('option', { value: o.value, selected: o.value === value }, o.label)),
  );
  return el('label', { class: 'filter' }, el('span', { class: 'filter__label' }, `${label}:`), select);
}

// --- tables -----------------------------------------------------------------

/**
 * Numbered pagination: "Showing 1–25 of 156 organisations" + ‹ 1 2 3 … 20 ›.
 * Renders into `slot` and calls onPage(n).
 */
export function renderPagination(slot, { page, pageSize, total, noun, onPage }) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const first = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const last = Math.min(total, page * pageSize);

  const nums = new Set([1, pages, page - 1, page, page + 1]);
  const list = [...nums].filter((n) => n >= 1 && n <= pages).sort((a, b) => a - b);
  const items = [];
  list.forEach((n, i) => {
    if (i > 0 && n - list[i - 1] > 1) items.push(el('span', { class: 'page-btn page-btn--gap' }, '…'));
    items.push(
      el(
        'button',
        {
          type: 'button',
          class: `page-btn${n === page ? ' is-current' : ''}`,
          'aria-current': n === page ? 'page' : null,
          onclick: () => n !== page && onPage(n),
        },
        String(n),
      ),
    );
  });

  render(
    slot,
    el(
      'div',
      { class: 'pagination' },
      el(
        'span',
        { class: 'pagination__info' },
        'Showing ',
        el('strong', {}, total === 0 ? '0' : `${first.toLocaleString()}–${last.toLocaleString()}`),
        ' of ',
        el('strong', {}, total.toLocaleString()),
        ` ${noun}`,
      ),
      pages > 1
        ? el(
            'div',
            { class: 'pagination__controls' },
            el('button', { type: 'button', class: 'page-btn page-btn--nav', 'aria-label': 'Previous page', disabled: page <= 1, onclick: () => onPage(page - 1) }, icon('chevron-left')),
            items,
            el('button', { type: 'button', class: 'page-btn page-btn--nav', 'aria-label': 'Next page', disabled: page >= pages, onclick: () => onPage(page + 1) }, icon('chevron-right')),
          )
        : null,
    ),
  );
}

/** Skeleton rows while a table loads — the design's grey bars, sized per column. */
export function skeletonRows(cols, rows = 6) {
  return Array.from({ length: rows }, () =>
    el('tr', { class: 'skeleton-row' }, Array.from({ length: cols }, (_, i) => el('td', {}, el('span', { class: `skeleton skeleton--${i === 0 ? 'wide' : 'mid'}` })))),
  );
}

/** Empty state: illustration tile, title, text, optional action. */
export function emptyState({ title, text, action, compact = false }) {
  return el(
    'div',
    { class: `empty-state${compact ? ' empty-state--compact' : ''}` },
    el('div', { class: 'empty-state__art' }, art('empty-box')),
    el('p', { class: 'empty-state__title' }, title),
    text ? el('p', { class: 'empty-state__text' }, text) : null,
    action ?? null,
  );
}

/** Error state with a retry — the design's "Failed to load users" panel. */
export function errorState({ title = 'Something went wrong', text, onRetry }) {
  return el(
    'div',
    { class: 'error-state' },
    el('div', { class: 'state-icon state-icon--red' }, icon('modal-question')),
    el('p', { class: 'empty-state__title' }, title),
    text ? el('p', { class: 'empty-state__text' }, text) : null,
    onRetry ? button('Try again', { variant: 'primary', onClick: onRetry }) : null,
  );
}

/** A table body row spanning every column — for empty/error/loading states inside a table. */
export function fullRow(cols, content) {
  return el('tr', { class: 'state-row' }, el('td', { colspan: String(cols) }, content));
}

// --- overlays ---------------------------------------------------------------

/**
 * Every open modal/drawer/menu registers a closer here. The router calls
 * closeAllOverlays() on each navigation: without it, a Back-button press while
 * a drawer is open changes the page underneath and leaves the drawer — still
 * bound to the previous organisation — floating over an unrelated screen.
 */
const overlayClosers = new Set();

export function closeAllOverlays() {
  for (const close of [...overlayClosers]) close();
  overlayClosers.clear();
}

// --- row action menu --------------------------------------------------------

let openMenu = null;

function closeMenu() {
  if (openMenu === null) return;
  overlayClosers.delete(closeMenu);
  const { panel, trigger, onDoc, onKey } = openMenu;
  panel.remove();
  trigger.classList.remove('is-open');
  trigger.setAttribute('aria-expanded', 'false');
  document.removeEventListener('mousedown', onDoc, true);
  document.removeEventListener('keydown', onKey, true);
  window.removeEventListener('scroll', closeMenu, true);
  window.removeEventListener('resize', closeMenu);
  openMenu = null;
}

/**
 * "⋯" trigger that opens the design's quick-action dropdown. items:
 * { label, iconName, danger, onSelect } — null/false entries are skipped, so
 * callers can write `canWrite() && {...}`.
 */
export function actionMenu(items, { label = 'Actions', iconName = 'more-vertical' } = {}) {
  const trigger = el('button', { type: 'button', class: 'btn-icon row-menu', 'aria-label': label, title: label, 'aria-haspopup': 'menu', 'aria-expanded': 'false' }, icon(iconName));

  trigger.addEventListener('click', (e) => {
    e.stopPropagation();
    if (openMenu?.trigger === trigger) {
      closeMenu();
      return;
    }
    closeMenu();
    const visible = items.filter(Boolean);
    if (visible.length === 0) return;

    const panel = el(
      'div',
      { class: 'menu', role: 'menu' },
      visible.map((item) =>
        el(
          'button',
          {
            type: 'button',
            role: 'menuitem',
            class: `menu__item${item.danger ? ' menu__item--danger' : ''}`,
            title: item.title ?? null,
            onclick: (ev) => {
              ev.stopPropagation();
              closeMenu();
              item.onSelect();
            },
          },
          item.iconName ? icon(item.iconName) : el('span', { class: 'menu__spacer' }),
          item.label,
        ),
      ),
    );
    document.body.append(panel);

    const r = trigger.getBoundingClientRect();
    const pw = panel.offsetWidth || 200;
    const ph = panel.offsetHeight || 0;
    const top = r.bottom + 6 + ph > window.innerHeight && r.top - ph - 6 > 0 ? r.top - ph - 6 : r.bottom + 6;
    panel.style.top = `${top + window.scrollY}px`;
    panel.style.left = `${Math.max(8, r.right - pw) + window.scrollX}px`;

    const onDoc = (ev) => {
      if (!panel.contains(ev.target) && ev.target !== trigger) closeMenu();
    };
    const onKey = (ev) => {
      if (ev.key === 'Escape') {
        closeMenu();
        trigger.focus();
      }
    };
    document.addEventListener('mousedown', onDoc, true);
    document.addEventListener('keydown', onKey, true);
    window.addEventListener('scroll', closeMenu, true);
    window.addEventListener('resize', closeMenu);
    trigger.classList.add('is-open');
    trigger.setAttribute('aria-expanded', 'true');
    openMenu = { panel, trigger, onDoc, onKey };
    overlayClosers.add(closeMenu);
    panel.querySelector('.menu__item')?.focus();
  });

  return trigger;
}

// --- modals -----------------------------------------------------------------

/**
 * The design's centred modal. Resolves true when confirmed, false when
 * cancelled. `onConfirm` may be async: the modal shows a busy state, and if it
 * throws, the error is shown IN the modal (it stays open) rather than an
 * alert — the operator keeps their context and can retry or cancel.
 *
 * tone: indigo | red | amber | green — the icon circle and confirm button.
 * body: extra content (inputs, lists). getValue: read the body's value before
 * confirming; returning `undefined` blocks confirmation.
 */
export function openModal({
  tone = 'indigo',
  iconName = 'modal-question',
  title,
  text,
  body,
  alert,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  confirmVariant,
  onConfirm,
  canConfirm,
}) {
  return new Promise((resolve) => {
    const errorBox = el('p', { class: 'modal__error', role: 'alert', hidden: true });
    const confirmBtn = button(confirmLabel, {
      variant: confirmVariant ?? (tone === 'red' ? 'danger' : tone === 'amber' ? 'warning' : 'primary'),
    });
    const cancelBtn = button(cancelLabel, { variant: 'secondary' });

    const dialog = el(
      'div',
      { class: 'modal', role: 'dialog', 'aria-modal': 'true', 'aria-label': title },
      el('div', { class: `state-icon state-icon--${tone}` }, icon(iconName)),
      el(
        'div',
        { class: 'modal__texts' },
        el('h2', { class: 'modal__title' }, title),
        text ? el('p', { class: 'modal__text' }, text) : null,
        body ?? null,
        alert
          ? el(
              'div',
              { class: 'modal__alert' },
              el('p', { class: 'modal__alert-title' }, icon('alert-question'), alert.title),
              alert.text ? el('p', { class: 'modal__alert-text' }, alert.text) : null,
            )
          : null,
        errorBox,
      ),
      el('div', { class: 'modal__actions' }, cancelBtn, confirmBtn),
    );
    const backdrop = el('div', { class: 'modal-backdrop' }, dialog);

    let busy = false;
    const previouslyFocused = document.activeElement;

    function close(result, { force = false } = {}) {
      if (busy && !force) return;
      overlayClosers.delete(forceClose);
      document.removeEventListener('keydown', onKey, true);
      backdrop.remove();
      if (!force && previouslyFocused instanceof HTMLElement) previouslyFocused.focus();
      resolve(result);
    }
    // Navigation away: the page this modal belonged to is gone, so it closes
    // even mid-request (the request itself still completes on the server).
    const forceClose = () => close(false, { force: true });
    overlayClosers.add(forceClose);

    function syncEnabled() {
      if (canConfirm) confirmBtn.disabled = busy || !canConfirm();
    }

    async function confirm() {
      if (busy || (canConfirm && !canConfirm())) return;
      errorBox.hidden = true;
      if (!onConfirm) {
        close(true);
        return;
      }
      busy = true;
      confirmBtn.disabled = true;
      cancelBtn.disabled = true;
      const original = confirmBtn.textContent;
      confirmBtn.textContent = 'Working…';
      try {
        await onConfirm();
        busy = false;
        close(true);
      } catch (err) {
        busy = false;
        errorBox.textContent = err?.message ?? 'Something went wrong. Try again.';
        errorBox.hidden = false;
        confirmBtn.textContent = original;
        cancelBtn.disabled = false;
        confirmBtn.disabled = false;
        syncEnabled();
      }
    }

    function onKey(e) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        close(false);
      } else if (e.key === 'Enter' && e.target instanceof HTMLInputElement) {
        e.preventDefault();
        void confirm();
      }
    }

    confirmBtn.addEventListener('click', () => void confirm());
    cancelBtn.addEventListener('click', () => close(false));
    backdrop.addEventListener('mousedown', (e) => {
      if (e.target === backdrop) close(false);
    });
    dialog.addEventListener('input', syncEnabled);
    dialog.addEventListener('change', syncEnabled);
    document.addEventListener('keydown', onKey, true);
    document.body.append(backdrop);
    syncEnabled();
    (dialog.querySelector('input, select, textarea') ?? confirmBtn).focus();
  });
}

/** Plain yes/no confirmation. */
export function confirmDialog(opts) {
  return openModal(opts);
}

/**
 * Typed confirmation — the operator must type `expected` (an org name, an
 * email) before the destructive button enables. The server re-checks it; this
 * only stops a mis-click from getting that far.
 */
export function typedConfirm({ expected, inputLabel, caseInsensitive = false, onConfirm, ...opts }) {
  const input = el('input', { class: 'input', type: 'text', placeholder: expected, autocomplete: 'off', spellcheck: 'false' });
  const norm = (v) => (caseInsensitive ? v.trim().toLowerCase() : v.trim());
  return openModal({
    ...opts,
    body: el('div', { class: 'field' }, el('label', {}, inputLabel), input),
    canConfirm: () => norm(input.value) === norm(expected),
    onConfirm: () => onConfirm(input.value.trim()),
  });
}

// --- drawer -----------------------------------------------------------------

/**
 * Right-hand slide-over (the design's Add User drawer). Returns { close }.
 * `footer` is optional; forms usually render their own submit row.
 */
export function openDrawer({ title, body, footer, onClose }) {
  const closeBtn = iconButton('drawer-close', { label: 'Close', onClick: () => close() });
  const panel = el(
    'aside',
    { class: 'drawer', role: 'dialog', 'aria-modal': 'true', 'aria-label': title },
    el('div', { class: 'drawer__head' }, el('h2', { class: 'drawer__title' }, title), closeBtn),
    el('div', { class: 'drawer__body' }, body),
    footer ? el('div', { class: 'drawer__foot' }, footer) : null,
  );
  const backdrop = el('div', { class: 'drawer-backdrop' }, panel);
  const previouslyFocused = document.activeElement;

  function onKey(e) {
    if (e.key === 'Escape') close();
  }
  function close() {
    if (!backdrop.isConnected) return;
    overlayClosers.delete(close);
    document.removeEventListener('keydown', onKey, true);
    backdrop.remove();
    if (previouslyFocused instanceof HTMLElement && previouslyFocused.isConnected) previouslyFocused.focus();
    onClose?.();
  }
  overlayClosers.add(close);

  backdrop.addEventListener('mousedown', (e) => {
    if (e.target === backdrop) close();
  });
  document.addEventListener('keydown', onKey, true);
  document.body.append(backdrop);
  (panel.querySelector('.drawer__body input, .drawer__body select') ?? closeBtn).focus();
  return { close, panel };
}

// --- toasts -----------------------------------------------------------------

let toastStack = null;

/** Bottom-right toast. tone: success | error | info. */
export function toast(message, { tone = 'success', timeout = 4500 } = {}) {
  if (toastStack === null || !document.body.contains(toastStack)) {
    toastStack = el('div', { class: 'toast-stack', role: 'status', 'aria-live': 'polite' });
    document.body.append(toastStack);
  }
  const node = el(
    'div',
    { class: `toast toast--${tone}` },
    el('span', { class: 'toast__icon' }, icon(tone === 'error' ? 'alert-question' : 'check-circle')),
    el('span', { class: 'toast__text' }, message),
    el('button', { type: 'button', class: 'toast__close', 'aria-label': 'Dismiss', onclick: () => node.remove() }, icon('drawer-close', { size: 14 })),
  );
  toastStack.append(node);
  if (timeout > 0) setTimeout(() => node.remove(), timeout);
  return node;
}

/** Surface an API failure the same way everywhere. */
export function toastError(err, fallback) {
  toast(err?.message ?? fallback ?? 'Something went wrong.', { tone: 'error', timeout: 7000 });
}
